import { useState } from 'react';
import { Card, Typography, Row, Col, Tag, Modal, Grid } from 'antd';
import spatialVisualization from '../assets/spatial-visualization.png';
import motifEnrichment from '../assets/motif-enrichment.png';
import cellTypeAnnotation from '../assets/cell-type-annotation.png';
import workflowDashboard from '../assets/workflow-dashboard.png';
import workflowBuilder from '../assets/workflow-builder.png';
import clusterEmbedding2D from '../assets/cluster-embedding-2d.png';
import clusterEmbedding3D from '../assets/cluster-embedding-3d.png';
import pathwayPlots from '../assets/pathway-plot.png';
import violinPlots from '../assets/violin-plot.png';
import cellrankPlot from '../assets/cellrank-plot.png';
import dynamoPlot from '../assets/dynamo-plot.png';
import heatmapPlot from '../assets/heatmap-plot.png';

const { Title, Paragraph, Text } = Typography;

const AnalysisGallery = () => {
    interface AnalysisItem {
        image: string;
        alt: string;
        title: string;
        description: string;
        tags: string[];
    }

    const [modalVisible, setModalVisible] = useState(false);
    const [selectedImage, setSelectedImage] = useState<AnalysisItem | null>(null);
    const { useBreakpoint } = Grid;
    const screens = useBreakpoint();

    const analysisItems: AnalysisItem[] = [
        {
            image: spatialVisualization,
            alt: "Spatial Transcriptomics Visualization",
            title: "Spatial Transcriptomics: spatial visualization",
            description: "Interactive spatial location of cells visualized by squidpy showing the spatial distribution of gene expression, cell cluster and other labels.",
            tags: ["Spatial transcriptome", "Squidpy", "Visualization"]
        },
        {
            image: motifEnrichment,
            alt: "Motif Enrichment Analysis",
            title: "scATAC: motif enrichment",
            description: "Identify and visualize differential peak region enriched transcription factor motifs using SnapATAC2.",
            tags: ["Epigenome", "SnapATAC2", "TF motif"]
        },
        {
            image: cellTypeAnnotation,
            alt: "Cell Type Annotation",
            title: "scRNA: cell type annotation",
            description: "Interactive cell type annotation panel with the help of AI.",
            tags: ["Transcriptome", "Clustering", "Cell Annotation"]
        },
        {
            image: workflowDashboard,
            alt: "Workflow Dashboard Interface",
            title: "Analysis Task Dashboard",
            description: "Centralized dashboard for managing and monitoring analysis workflows and tasks.",
            tags: ["Dashboard", "Management"]
        },
        {
            image: workflowBuilder,
            alt: "Workflow Builder Interface",
            title: "Computational Pipeline Design",
            description: "Visual workflow builder for constructing reproducible analysis pipelines.",
            tags: ["Pipeline", "Workflow"]
        },
        {
            image: clusterEmbedding2D,
            alt: "2D Cluster Embedding",
            title: "2D Cluster Analysis",
            description: "High-resolution 2D UMAP with comprehensive cluster annotation.",
            tags: ["2D", "Clusters"]
        },
        {
            image: clusterEmbedding3D,
            alt: "3D Cluster Embedding",
            title: "3D Cluster Visualization",
            description: "Interactive 3D embedding with detailed cluster analysis and filtering.",
            tags: ["3D", "Clusters"]
        },
        {
            image: pathwayPlots,
            alt: "Volcano Plot Gene Expression",
            title: "Volcano Plot: Differential Expression",
            description: "Statistical volcano plot showing significance vs fold change for differential gene expression with pathway enrichment annotations.",
            tags: ["Volcano Plot", "Statistics", "P-value"]
        },
        {
            image: violinPlots,
            alt: "Violin Plot Distributions",
            title: "Violin Plot: Cell Type Distribution Analysis",
            description: "Violin plots showing gene expression distributions across cell types.",
            tags: ["Violin", "Distribution"]
        },
        {
            image: cellrankPlot,
            alt: "CellRank Trajectory Analysis",
            title: "Network Plot: CellRank Trajectories",
            description: "Interactive network visualization of CellRank random walks showing cellular state transitions and trajectory inference.",
            tags: ["Network", "CellRank", "Trajectories"]
        },
        {
            image: dynamoPlot,
            alt: "Dynamo Velocity Analysis",
            title: "3D Streamplot: RNA Velocity Field",
            description: "Three-dimensional streamline plot visualizing RNA velocity vectors and cellular dynamics.",
            tags: ["Streamplot", "3D", "RNA Velocity"]
        },
        {
            image: heatmapPlot,
            alt: "Gene Expression Heatmap",
            title: "Heatmap: Clustered Gene Expression",
            description: "Hierarchically clustered heatmap matrix displaying gene expression profiles across cell clusters with dendrogram.",
            tags: ["Heatmap", "Clustering", "Matrix"]
        }
    ];

    const handleImageClick = (item: AnalysisItem) => {
        setSelectedImage(item);
        setModalVisible(true);
    };

    const handleModalClose = () => {
        setModalVisible(false);
        setSelectedImage(null);
    };

    return (
        <div style={{ marginBottom: 32 }}>
            <div style={{ marginBottom: 24 }}>
                <Title level={4} style={{
                    color: '#1a365d',
                    fontSize: screens.xs ? 16 : 18,
                    fontWeight: 600,
                    marginBottom: 8
                }}>
                    Analysis Methods Gallery
                </Title>
                <Text style={{
                    fontSize: screens.xs ? 13 : 14,
                    color: '#4a5568',
                    lineHeight: 1.5
                }}>
                    Interactive demonstrations of computational workflows for single-cell genomics analysis
                </Text>
            </div>

            <div style={{
                maxHeight: screens.xs ? '400px' : '520px',
                overflowY: 'auto',
                paddingRight: '8px',
                scrollbarWidth: 'thin',
                scrollbarColor: '#cbd5e0 #f7fafc'
            }}>
                <Row gutter={[screens.xs ? 12 : 16, screens.xs ? 12 : 16]}>
                    {analysisItems.map((item, index) => (
                        <Col xs={24} sm={12} md={8} lg={8} key={index}>
                            <Card
                                hoverable
                                style={{
                                    border: '1px solid #e2e8f0',
                                    borderRadius: 6,
                                    height: '100%'
                                }}
                                styles={{ body: { padding: screens.xs ? '12px' : '16px' } }}
                            >
                                <div
                                    style={{
                                        height: screens.xs ? 120 : 160,
                                        background: '#f7fafc',
                                        borderRadius: 4,
                                        marginBottom: 12,
                                        border: '1px solid #e2e8f0',
                                        overflow: 'hidden',
                                        cursor: 'pointer',
                                        position: 'relative'
                                    }}
                                    onClick={() => handleImageClick(item)}
                                >
                                    <img
                                        src={item.image}
                                        alt={item.alt}
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                            transition: 'transform 0.2s ease'
                                        }}
                                    />
                                </div>
                                <Title level={5} style={{
                                    fontSize: screens.xs ? 12 : 13,
                                    color: '#2d3748',
                                    marginBottom: 6,
                                    fontWeight: 600,
                                    lineHeight: 1.3
                                }}>
                                    {item.title}
                                </Title>
                                <Paragraph style={{
                                    fontSize: screens.xs ? 10 : 11,
                                    color: '#4a5568',
                                    marginBottom: 10,
                                    lineHeight: 1.4
                                }}>
                                    {item.description}
                                </Paragraph>
                                <div>
                                    {item.tags.map((tag, tagIndex) => (
                                        <Tag key={tagIndex} style={{
                                            fontSize: screens.xs ? 8 : 9,
                                            background: '#edf2f7',
                                            border: '1px solid #e2e8f0',
                                            color: '#4a5568',
                                            margin: '1px 2px'
                                        }}>
                                            {tag}
                                        </Tag>
                                    ))}
                                </div>
                            </Card>
                        </Col>
                    ))}
                </Row>
            </div>

            <div style={{
                textAlign: 'center',
                marginTop: 24,
                padding: screens.xs ? '12px' : '16px',
                background: '#f7fafc',
                borderRadius: 6,
                border: '1px solid #e2e8f0'
            }}>
                <Text style={{
                    fontSize: screens.xs ? 11 : 12,
                    color: '#4a5568',
                    fontStyle: 'italic'
                }}>
                    Interactive demonstrations available in the full platform interface • Click images to view larger versions
                </Text>
            </div>

            {/* Image Modal */}
            <Modal
                open={modalVisible}
                onCancel={handleModalClose}
                footer={null}
                width={screens.xs ? "100%" : "90%"}
                style={{
                    maxWidth: screens.xs ? "100vw" : '1200px',
                    top: screens.xs ? 0 : 'auto'
                }}
                centered={!screens.xs}
                styles={{ body: { padding: screens.xs ? '16px' : '24px' } }}
            >
                {selectedImage && (
                    <div>
                        <div style={{
                            textAlign: 'center',
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            background: '#f7fafc'
                        }}>
                            <img
                                src={selectedImage.image}
                                alt={selectedImage.alt}
                                style={{
                                    width: '100%',
                                    height: 'auto',
                                    maxHeight: screens.xs ? '50vh' : '70vh',
                                    objectFit: 'contain'
                                }}
                            />
                        </div>
                        <div style={{
                            textAlign: 'center',
                            marginTop: '16px'
                        }}>
                            {selectedImage.tags.map((tag, tagIndex) => (
                                <Tag key={tagIndex} style={{
                                    fontSize: screens.xs ? '10px' : '12px',
                                    background: '#edf2f7',
                                    border: '1px solid #e2e8f0',
                                    color: '#4a5568',
                                    margin: '4px'
                                }}>
                                    {tag}
                                </Tag>
                            ))}
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default AnalysisGallery;